import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuthStore, User } from '@/shared/stores/authStore';
import { useToastStore } from '@/shared/stores/toastStore';
import {
    CustomButton,
    CustomInput,
} from '@/shared/components/atoms';
import { Mail, Lock, Eye, EyeOff, ShieldCheck, LayoutDashboard, Phone, AlertCircle, RefreshCw, Ticket, BookOpen, Bell, Users, Clock } from 'lucide-react';
import raabtaLogo from '@/assets/raabtalogo_white_bg.png';
import { faSignInAlt, faArrowLeft, faKey } from '@fortawesome/free-solid-svg-icons';
import authService from '../api/auth.service';
import { getMediaUrl } from '@/shared/utils/media';
import './LoginPage.css';

// Login Validation Schema
const loginSchema = z.object({
    email: z.string().min(1, 'Email is required').email('Invalid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

// Helper to extract tenant slug from URL
const getTenantSlug = () => {
    const urlParams = new URLSearchParams(window.location.search);
    // Check for school_slug query parameter first
    const tenantParam = urlParams.get('school_slug');
    if (tenantParam) return tenantParam;

    const hostname = window.location.hostname;
    
    if (hostname === 'localhost' || /^(\d{1,3}\.){3}\d{1,3}$/.test(hostname)) {
        return null;
    }

    const parts = hostname.split('.');
    
    if (hostname.endsWith('.localhost') && parts.length > 1) {
        return parts[0];
    }
    
    if (parts.length >= 3) {
        const slug = parts[0];
        const ignoredSubdomains = ['www', 'admin', 'app', 'resolveiq360', 'resolveiq360be'];
        if (!ignoredSubdomains.includes(slug.toLowerCase())) {
            return slug;
        }
    }
    
    return null;
};

/**
 * Professional Login Page
 * A high-end entry point for the multi-tenant ticketing system.
 */
export const LoginPage = () => {
    const navigate = useNavigate();
    const setAuth = useAuthStore((state) => state.setAuth);
    const setTenant = useAuthStore((state) => state.setTenant);
    const { showToast } = useToastStore();

    const [view, setView] = useState<'login' | 'forgot'>('login');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [schoolConfig, setSchoolConfig] = useState<any>(null);
    const [isConfigLoading, setIsConfigLoading] = useState(true);
    const [configError, setConfigError] = useState<string | null>(null);
    const [forgotEmail, setForgotEmail] = useState('');

    useEffect(() => {
        const fetchConfig = async () => {
            const slug = getTenantSlug();
            if (slug) {
                try {
                    const response = await authService.getPublicSchoolConfig(slug);
                    if (response.success && response.data) {
                        setSchoolConfig(response.data);
                        setTenant(response.data); // Persist configuration globally - App.tsx will pick this up
                    } else {
                        setConfigError(response.error || response.message || `We couldn't find a school with the slug "${slug}". Please check the link and try again.`);
                    }
                } catch (error: any) {
                    console.error("Failed to load school config:", error);
                    // Extract the actual error message from the API response
                    const apiError = error?.response?.data?.error || error?.response?.data?.message;
                    const status = error?.response?.status;
                    if (apiError) {
                        setConfigError(apiError);
                    } else if (status === 404) {
                        setConfigError(`We couldn't find a school with the slug "${slug}". Please check the link and try again.`);
                    } else if (!error?.response) {
                        setConfigError('Unable to connect to the portal server. The service may be offline or unreachable. Please try again later.');
                    } else {
                        setConfigError(`Something went wrong while loading school information (error ${status || 'unknown'}). Please try again.`);
                    }
                }
            } else {
                // No tenant slug — reset to default branding
                setTenant(null as any);
                setSchoolConfig(null);
            }
            setIsConfigLoading(false);
        };
        fetchConfig();
    }, [setTenant]);

    // React Hook Form
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormValues>({
        resolver: zodResolver(loginSchema),
        defaultValues: {
            email: '',
            password: '',
        }
    });

    const onLoginSubmit = async (data: LoginFormValues) => {
        setIsLoading(true);

        try {
            const slug = getTenantSlug();
            const response = await authService.login(data, slug);

            if (response.success && response.data) {
                const {
                    access_token,
                    refresh_token,
                    ...userDataFromApi
                } = response.data;

                const role = userDataFromApi.role?.toUpperCase();

                // ── Role-based access control ──────────────────────────
                if (slug) {
                    // School portal: only SCHOOL_ADMIN and EMPLOYEE allowed
                    if (role === 'PARENT') {
                        showToast(
                            'Parents are not allowed to login here.',
                            'error',
                            'Access Denied'
                        );
                        setIsLoading(false);
                        return;
                    }
                    if (role === 'SUPER_ADMIN') {
                        showToast(
                            'Super Admins must login through the main admin portal.',
                            'error',
                            'Access Denied'
                        );
                        setIsLoading(false);
                        return;
                    }
                } else {
                    // Root / Super Admin portal: only SUPER_ADMIN allowed
                    if (role !== 'SUPER_ADMIN') {
                        showToast(
                            'This portal is for Super Admins only. Please use your school portal to login.',
                            'error',
                            'Access Denied'
                        );
                        setIsLoading(false);
                        return;
                    }
                }
                // ────────────────────────────────────────────────────────

                // Store tokens
                localStorage.setItem('auth_token', access_token);
                localStorage.setItem('refresh_token', refresh_token);

                // Prepare user data for store
                const userData: User = {
                    ...userDataFromApi,
                    role,
                    avatar: `https://ui-avatars.com/api/?name=${userDataFromApi.name}&background=6366f1&color=fff`,
                };

                setAuth(userData);
                showToast('Successfully logged in!', 'success', 'Welcome');
                navigate('/');
            } else {
                showToast(response.message || response.error || 'Login failed. Please check your credentials.', 'error', 'Authentication Error');
            }
        } catch (err: any) {
            console.error('Login error:', err);
            const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Authentication failed.';
            showToast(errorMessage, 'error', 'Authentication Error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!forgotEmail) {
            showToast('Please enter your email address.', 'error', 'Error');
            return;
        }

        setIsLoading(true);
        try {
            const slug = getTenantSlug();
            const response = await authService.forgotPassword(forgotEmail, slug);
            
            if (response.success !== false) {
                showToast(`Reset link sent to ${forgotEmail}`, 'success', 'Password Reset');
                setView('login');
            } else {
                showToast(response.error || response.message || 'Failed to send reset link.', 'error', 'Error');
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to send reset link.';
            showToast(errorMessage, 'error', 'Error');
        } finally {
            setIsLoading(false);
        }
    };

    if (isConfigLoading) {
        return (
            <div className="login-page-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: '1rem' }}>
                 <div className="brand-logo" style={{ animation: 'pulse 2s infinite', display: 'flex', justifyContent: 'center', background: 'none', boxShadow: 'none', width: '56px', height: '56px' }}>
                     <img src={raabtaLogo} alt="Raabta Logo" style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'contain' }} />
                 </div>
                 <p style={{ color: 'var(--text-secondary, #64748b)' }}>Loading Portal...</p>
            </div>
        );
    }

    if (configError) {
        return (
            <div className="login-page-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '1rem' }}>
                <div style={{
                    maxWidth: '460px', width: '100%',
                    background: 'white', borderRadius: '20px',
                    border: '1px solid rgba(15, 23, 42, 0.08)',
                    padding: '2.5rem 2rem',
                    textAlign: 'center',
                    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.08)'
                }}>
                    <div style={{
                        width: '70px', height: '70px', borderRadius: '20px',
                        background: 'rgba(239, 68, 68, 0.1)',
                        color: '#ef4444',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: '1.25rem'
                    }}>
                        <AlertCircle size={32} />
                    </div>
                    <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f172a', margin: '0 0 0.5rem', letterSpacing: '-0.02em' }}>
                        School not found
                    </h2>
                    <p style={{ color: '#64748b', fontSize: '0.92rem', lineHeight: 1.6, margin: '0 0 1.5rem' }}>
                        {configError}
                    </p>
                    <div style={{ display: 'flex', gap: '0.65rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: '0.45rem',
                                padding: '0.7rem 1.25rem', borderRadius: '10px',
                                background: 'var(--accent-primary, #6366f1)',
                                color: 'white', border: 'none', cursor: 'pointer',
                                fontSize: '0.875rem', fontWeight: 700,
                                boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
                                fontFamily: 'inherit'
                            }}
                        >
                            <RefreshCw size={14} /> Try again
                        </button>
                        {/* <button
                            onClick={() => window.location.href = '/'}
                            style={{
                                padding: '0.7rem 1.25rem', borderRadius: '10px',
                                background: 'transparent',
                                color: '#475569',
                                border: '1px solid rgba(15, 23, 42, 0.12)',
                                cursor: 'pointer',
                                fontSize: '0.875rem', fontWeight: 700,
                                fontFamily: 'inherit'
                            }}
                        >
                            Back to home
                        </button> */}
                    </div>
                    <p style={{ fontSize: '0.78rem', color: '#94a3b8', marginTop: '1.5rem', marginBottom: 0 }}>
                        If you keep seeing this error, contact your school administrator.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="login-page-wrapper">
            {/* Visual Background Elements */}
            <div className="login-bg">
                <div className="login-bg__circle login-bg__circle--1" />
                <div className="login-bg__circle login-bg__circle--2" />
                <div className="login-bg__circle login-bg__circle--3" />
            </div>

            {/* Left Side: Branding & Quick Info */}
            <div className="login-info-side" style={schoolConfig?.banner_url ? { backgroundImage: `url(${getMediaUrl(schoolConfig.banner_url)})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' } : {}}>
                {/* Overlay for readability when banner is present */}
                {schoolConfig?.banner_url && (
                    <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.5) 100%)', zIndex: 0 }} />
                )}
                <div className="info-content" style={{ position: 'relative', zIndex: 1 }}>
                    <div className="brand-header">
                        <div className="brand-logo" style={{ background: 'none', boxShadow: 'none', width: '56px', height: '56px' }}>
                            {schoolConfig?.logo_url ? (
                                <img src={getMediaUrl(schoolConfig.logo_url)} alt={schoolConfig.name} style={{ width: 52, height: 52, objectFit: 'contain', borderRadius: '50%' }} />
                            ) : (
                                <img src={raabtaLogo} alt="Raabta Logo" style={{ width: 52, height: 52, objectFit: 'contain', borderRadius: '50%' }} />
                            )}
                        </div>
                        <span className="brand-name" style={schoolConfig?.banner_url ? { color: '#ffffff' } : {}}>{schoolConfig?.name || 'Raabta'}</span>
                    </div>

                    <div className="hero-text">
                        <h1 className="hero-title" style={schoolConfig?.banner_url ? { color: '#ffffff' } : {}}>
                            {schoolConfig ? (
                                <>Welcome to <span className="text-gradient" style={schoolConfig?.banner_url ? { background: 'linear-gradient(to right, #ffffff 20%, #e0e7ff 80%)', WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' } : {}}>{schoolConfig.name}</span></>
                            ) : (
                                <>Digital <span className="text-gradient">School Support.</span></>
                            )}
                        </h1>
                        <p className="hero-subtitle" style={schoolConfig?.banner_url ? { color: 'rgba(255,255,255,0.85)' } : {}}>
                            {schoolConfig?.tagline || 'Empowering modern educational institutions with a high-performance enterprise ticketing and helpdesk ecosystem.'}
                        </p>
                    </div>

                    {/* Support info when school config is available */}
                    {schoolConfig && (schoolConfig.support_email || schoolConfig.support_phone) && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '1.5rem' }}>
                            {schoolConfig.support_email && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: schoolConfig.banner_url ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)' }}>
                                    <Mail size={14} />
                                    <span>{schoolConfig.support_email}</span>
                                </div>
                            )}
                            {schoolConfig.support_phone && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: schoolConfig.banner_url ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)' }}>
                                    <Phone size={14} />
                                    <span>{schoolConfig.support_phone}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Feature capsules only shown for default (no school config) */}
                    {!schoolConfig && (
                        <div className="features-capsules">
                            <div className="feature-capsule">
                                <Ticket size={16} /> <span>Support Tickets</span>
                            </div>
                            <div className="feature-capsule">
                                <BookOpen size={16} /> <span>Knowledge Base</span>
                            </div>
                            <div className="feature-capsule">
                                <Users size={16} /> <span>Role-Based Access</span>
                            </div>
                            <div className="feature-capsule">
                                <Clock size={16} /> <span>Timely Support</span>
                            </div>
                            <div className="feature-capsule">
                                <Bell size={16} /> <span>Real-Time Alerts</span>
                            </div>
                            <div className="feature-capsule">
                                <LayoutDashboard size={16} /> <span>Analytics Dashboard</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Right Side: Auth Form */}
            <div className="login-form-side">
                <div className="login-form-container">
                    {/* Mobile & Tablet Header (Visible on screens <= 1023px) */}
                    <div className="mobile-brand-header">
                        <div className="mobile-brand-logo">
                            {schoolConfig?.logo_url ? (
                                <img src={getMediaUrl(schoolConfig.logo_url)} alt={schoolConfig.name || 'School Logo'} />
                            ) : (
                                <img src={raabtaLogo} alt="Raabta Logo" />
                            )}
                        </div>
                    </div>

                    {/* Login View */}
                    <div style={{ display: view === 'login' ? 'block' : 'none' }}>
                        <div className="login-form-header">
                            <h2 className="login-welcome">Welcome Back</h2>
                            <p className="login-instructions">Securely access your institution portal</p>
                        </div>

                        <form className="login-form" onSubmit={handleSubmit(onLoginSubmit)}>
                            <CustomInput
                                label="Email"
                                placeholder="name@company.com"
                                type="email"
                                leftIcon={<Mail size={18} />}
                                error={errors.email?.message}
                                {...register('email')}
                            />

                            <div className="form-group">
                                <CustomInput
                                    label='Password'
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    leftIcon={<Lock size={18} />}
                                    error={errors.password?.message}
                                    rightIcon={
                                        <div
                                            className="password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </div>
                                    }
                                    {...register('password')}
                                />
                                {schoolConfig && (
                                    <button
                                        type="button"
                                        className="forgot-link"
                                        onClick={() => setView('forgot')}
                                    >
                                        Forgot Password?
                                    </button>
                                )}
                            </div>

                            <CustomButton
                                label="Sign In to Portal"
                                type="submit"
                                fullWidth
                                size="medium"
                                loading={isLoading}
                                faIcon={faSignInAlt}
                                iconPosition="right"
                                className="login-submit"
                            />
                        </form>
                    </div>

                    {/* Forgot Password View */}
                    <div style={{ display: view === 'forgot' ? 'block' : 'none' }}>
                        <div className="login-form-header">
                            <h2 className="login-welcome">Reset Password</h2>
                            <p className="login-instructions">Enter your email to receive a reset link</p>
                        </div>

                        <form className="login-form" onSubmit={handleForgotSubmit}>
                            <CustomInput
                                name="forgotEmail"
                                label="Recovery Email"
                                placeholder="name@company.com"
                                type="email"
                                leftIcon={<Mail size={18} />}
                                value={forgotEmail}
                                onChange={(e) => setForgotEmail(e.target.value)}
                            />

                            <CustomButton
                                label="Send Reset Link"
                                type="submit"
                                fullWidth
                                size="medium"
                                loading={isLoading}
                                faIcon={faKey}
                                iconPosition="right"
                                className="login-submit"
                            />

                            <CustomButton
                                label="Back to Login"
                                type="button"
                                fullWidth
                                size="medium"
                                variant='text'
                                faIcon={faArrowLeft}
                                iconPosition="left"
                                className="back-to-login"
                                onClick={() => setView('login')}
                            />
                        </form>
                    </div>

                    {schoolConfig && (
                        <div className="login-footer">
                            <p>Need help? <button type="button" className="contact-link" onClick={() => navigate('/contact')}>Contact Support</button></p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
